import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Button,
  Tag,
  Tooltip,
  Space,
  Modal,
  DatePicker,
  Form,
  Input,
} from "antd";
import {
  CustomButton,
  CustomSecondaryButton,
  CustomTitle,
  CustomSubTitle,
} from "../physician_page/styled";
import {
  PlusOutlined,
  SettingOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  VideoCameraOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { ModalWrapper, StyledTable } from "../admin/videos/styled";
import { patientService } from "../../../services/user";
import AddPatientModal from "./AddPatientModal";
import { notify, debounce } from "../../../utils/helper";
import DeleteConfirmationModal from "../physician_page/component/DeleteConfirmationModal";
import PhysicianDetailModal from "../physician_page/component/PhysicianDetailModal";
import { capitalizeFirstLetter } from "../../../utils/utils";
import AssignVideoToPatientModal from "./AssignVideoToPatientModal";
import { extractToken } from "../../../utils/localStorage";
import moment from "moment";
import { CustomSearchContainer } from "../../table-item";
import Filter from "../../table-item/filter/Filter";
import { FiSearch } from "react-icons/fi";
import { globalRoles } from "../../../utils/roles";
import AddPhysicianModalWrapper from "../../modal/AddPhysicianModalWrapper";

// New Calendar Modal Component
const ScheduleAppointmentModal = ({
  isOpen,
  setOpen,
  patientId,
  patientName,
}) => {
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleSave = async () => {
    try {
      setConfirmLoading(true);
      const values = await form.validateFields();

      // Here you would save the appointment data to your backend
      // For example: await appointmentService.create({ patientId, date: values.appointmentDate })

      // console.log("Scheduling appointment for patient:", patientId);
      // console.log(
      //   "Appointment date:",
      //   values.appointmentDate.format("YYYY-MM-DD HH:mm")
      // );

      notify("success", "Appointment scheduled successfully");
      setOpen(false);
    } catch (error) {
      console.error("Error scheduling appointment:", error);
      notify("error", "Failed to schedule appointment");
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <AddPhysicianModalWrapper
      title={
        <div className="row-flex flex-column">
          <h3 className="font-18 font-semibold text-primary mb-5">
            Schedule Appointment
          </h3>
        </div>
      }
      show={isOpen}
      setShow={() => setOpen(false)}
      confirmLoading={confirmLoading}
      footer={null}
      keyClose={false}
      className="custom-detail-modal"
    >
      <Form form={form} layout="vertical" onFinish={handleSave}>
        <Form.Item label={`Schedule an appointment for ${patientName}`}>
          <p className="text-primary">Select a date and time for the appointment:</p>
        </Form.Item>
        <Form.Item
          name="appointmentDate"
          rules={[
            {
              required: true,
              message: "Please select appointment date and time",
            },
          ]}
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            style={{ width: "100%" }}
            disabledDate={(current) =>
              current && current < moment().startOf("day")
            }
          />
        </Form.Item>

        <Row>
          <Col span={24} className="d-flex justify-end gap-12">
            <CustomSecondaryButton
              onClick={() => setOpen(false)}
            >
              Cancel
            </CustomSecondaryButton>
            <CustomButton htmlType="submit">
              Save Appointment
            </CustomButton>
          </Col>
        </Row>
      </Form>
    </AddPhysicianModalWrapper>
  );
};
//NOT IN USE
const PatientsPage = () => {
  const [isModalOpen, setisModalOpen] = useState(false);
  const [allpatient, setAllpatient] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(false);
  const [isViewDetail, setIsViewDetail] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [patientDataById, setPatientDataById] = useState([]);
  const [patientEdit, setPatientEdit] = useState(false);
  const [patientId, setPatientId] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 7,
    total: 0,
  });
  const [count, setCount] = useState(0);

  // New states for the calendar functionality
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [selectedPatientForScheduling, setSelectedPatientForScheduling] =
    useState(null);
  const [selectedPatientName, setSelectedPatientName] = useState("");
  const [searchText, setSearchText] = useState("");
  // const [show, setShow] = useState(false);
  // const [filterTab, setFilterTab] = useState([]);
  const [sortingColumn, setSortingColumn] = useState("");

  const user = extractToken();

  // States for checkbox functionality
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isAssignVideoModalOpen, setIsAssignVideoModalOpen] = useState(false);

  const getStatusTag = (status) => {
    return (
      <Tag color={status ? "green" : "red"}>
        {status ? "Active" : "Inactive"}
      </Tag>
    );
  };

  const fetchPatient = async (searchInput=searchText) => {
    try {
      setLoading(true);
      const response = await patientService.getAll(
        pagination?.pageSize,
        pagination?.current,
        searchInput,
        sortingColumn,
        user?.role === globalRoles.SUPERADMIN ? "true" : "", // physician parameter
        "" // ccm parameter - empty for regular patients page
      );
      const processedData = processData(response?.data?.results || []);
      setAllpatient(processedData);
      setCount(response?.data?.totalResults);
    } catch (error) {
      console.error("Error fetching patient:", error);
    } finally {
      setLoading(false);
    }
  };  

  const processData = (data) => {
    return data.map((item) => ({
      ...item,
      name: capitalizeFirstLetter(item.name),
      gender: item.gender ? capitalizeFirstLetter(item.gender) : "-",
      statusValue: item.status,
      key: item.id,
    }));
  };

  useEffect(() => {
    fetchPatient();
  }, [pagination, sortingColumn]);

  const handleDelete = async () => {
    try {
      await patientService.delete(selectedId);
      fetchPatient();
      setIsDeleteModalOpen(false);
      notify("success", "Patient Delete Successfully.");
    } catch (error) {
      console.error("Error deleting library manager:", error);
    }
  };

  const getPatientById = async () => {
    try {
      const res = await patientService.getById(selectedPatientId);
      setPatientDataById(res?.data);
    } catch (err) {}
  };

  useEffect(() => {
    if (selectedPatientId) {
      getPatientById();
    }
  }, [selectedPatientId]);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleAssignVideoClick = () => {
    setIsAssignVideoModalOpen(true);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "fullName",
      sorter: (a, b) => (a.fullName || "").localeCompare(b.fullName || ""),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    // {
    //   title: "Date of Birth",
    //   dataIndex: "dob",
    //   align: "center",
    //   key: "dob",
    //   render: (dob) => (dob ? moment(dob).format("DD MMM YYYY") : "N/A"),
    // },
    // {
    //   title: "Gender",
    //   dataIndex: "gender",
    //   align: "center",
    //   key: "gender",
    // },
    {
      title: "Email Verified",
      dataIndex: "isEmailVerified",
      align: "center",
      key: "isEmailVerifiedText",
      sorter: (a,b) => (a.isEmailVerifiedText || "").localeCompare(b.isEmailVerifiedText || ""),
      // filters: [
      //   { text: "Verified", value: true },
      //   { text: "Not Verified", value: false },
      // ],
      // onFilter: (value, record) => record.isEmailVerified === value,
      render: (verified) => (
        <Tag color={verified ? "green" : "orange"}>
          {verified ? "Verified" : "Not Verified"}
        </Tag>
      ),
    },
    // {
    //   title: "Status",
    //   dataIndex: "status",
    //   align: "center",
    //   key: "status",
    //   filters: [
    //     { text: "Active", value: true },
    //     { text: "Inactive", value: false },
    //   ],
    //   onFilter: (value, record) => record.status === value,
    //   render: (status) => getStatusTag(status),
    // },
    {
      title: "Created At",
      dataIndex: "createdAt",
      align: "center",
      key: "createdAt",
      render: (date) =>
        date ? moment(date).format("DD MMM YYYY hh:mm A") : "N/A",
    },
    {
      title: "Actions",
      align: "center",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View Details">
            <div
              onClick={() => {
                setSelectedPatientId(record?.id);
                setIsViewDetail(true);
              }}
              className="cursor"
            >
              <EyeOutlined />
            </div>
          </Tooltip>

          {/* New Calendar Icon */}
          <Tooltip title="Schedule Appointment">
            <div
              onClick={() => {
                setSelectedPatientForScheduling(record?.id);
                setSelectedPatientName(record?.name);
                setIsCalendarModalOpen(true);
              }}
              className="cursor"
            >
              <CalendarOutlined />
            </div>
          </Tooltip>

          {user?.role === globalRoles.SUPERADMIN && (
            <>
              <Tooltip title="Edit">
                <div
                  onClick={() => {
                    setPatientEdit(true);
                    setPatientId(record?.id);
                    setisModalOpen(true);
                  }}
                  className="cursor"
                >
                  <EditOutlined />
                </div>
              </Tooltip>
              <Tooltip title="Delete">
                <div
                  onClick={() => {
                    setSelectedId(record?.id);
                    setIsDeleteModalOpen(true);
                  }}
                  className="cursor"
                >
                  <DeleteOutlined />
                </div>
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  const handlePagination = (page) => {
    setPagination((prev) => ({ ...prev, current: page }));
  };

  const handleSearch = useCallback(
    debounce((value) => {
      fetchPatient(value, true);
    }, 500),
    []
  );

  const handleChangeTable = (a, b, c) => {
   const sortColumn = c?.column?.dataIndex;
   const sortOrder = c?.order;

  if (sortColumn && sortOrder) {
    const normalizedOrder = sortOrder === "ascend" ? "asc" : "desc";
    setSortingColumn(`${sortColumn}:${normalizedOrder}`);
  } else {
    setSortingColumn("");
  }
  };

  const hasData = allpatient.length > 0;
  const scroll = hasData ? { x: "max-content" } : null;

  return (
    <>
      {isModalOpen && (
        <AddPatientModal
          isOpen={isModalOpen}
          setOpen={setisModalOpen}
          isEdit={patientEdit}
          setEdit={setPatientEdit}
          patientId={patientId}
          fetchPatient={fetchPatient}
        />
      )}

      {isViewDetail && (
        <PhysicianDetailModal
          isOpen={isViewDetail}
          setOpen={setIsViewDetail}
          DataById={patientDataById}
          title={"Patient Details"}
          subtitle={"View the details of the patient."}
        />
      )}

      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          setOpen={setIsDeleteModalOpen}
          onConfiem={() => handleDelete()}
        />
      )}

      {isAssignVideoModalOpen && (
        <AssignVideoToPatientModal
          isOpen={isAssignVideoModalOpen}
          setOpen={setIsAssignVideoModalOpen}
          selectedPatientIds={selectedRowKeys}
          onSuccess={() => {
            setSelectedRowKeys([]);
            notify("success", "Video assigned successfully");
          }}
          setSelectedRowKeys={setSelectedRowKeys}
        />
      )}

      {/* New Calendar Modal */}
      {isCalendarModalOpen && (
        <ScheduleAppointmentModal
          isOpen={isCalendarModalOpen}
          setOpen={setIsCalendarModalOpen}
          patientId={selectedPatientForScheduling}
          patientName={selectedPatientName}
        />
      )}

      <Row style={{ padding: "40px 60px" }}>
        <Row className="custom-header w-100 mb-15">
          <Col className="header-item">
            <CustomTitle>Patients</CustomTitle>
            <CustomSubTitle>
              Manage your patients and send them educational videos.
            </CustomSubTitle>
          </Col>
          <Col className="custom-btn">
            {selectedRowKeys.length > 0 && (
              <CustomButton
                type="primary"
                icon={<VideoCameraOutlined />}
                onClick={handleAssignVideoClick}
                style={{ marginRight: 10 }}
              >
                Assign Video ({selectedRowKeys.length} selected)
              </CustomButton>
            )}
            <CustomButton onClick={() => setisModalOpen(true)}>
              <PlusOutlined className="font-18 mt-5" />
              Add Patients
            </CustomButton>
            {/* <CustomSecondaryButton>
              <SettingOutlined className="font-18" />
            </CustomSecondaryButton> */}
          </Col>
        </Row>

        <Col
          span={24}
          className="d-flex"
          style={{ justifyContent: "space-between" }}
        >
          <div style={{ width: "25%" }}>
            <CustomSearchContainer>
              <FiSearch style={{position: "absolute", zIndex: 1}} className="icon ml-5" />
              <Input
                placeholder="Search here..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  handleSearch(e.target.value);
                }}
              />
            </CustomSearchContainer>
          </div>
          {/* <div>
            <Filter
              show={show}
              setShow={setShow}
              data={[
                {
                  title: "Email Status",
                  list: ["Verified", "Not Verified"],
                  filterList: filterTab,
                  setFilterList: setFilterTab,
                },
              ]}
            />
          </div> */}
        </Col>

        <Col span={24} style={{ flex: 1, padding: "24px 0px" }}>
          <StyledTable
            rowSelection={rowSelection}
            columns={columns}
            dataSource={allpatient}
            rowKey="id"
            scroll={scroll}
            pagination={{
              current: pagination?.current,
              pageSize: pagination?.pageSize,
              showSizeChanger: false,
              total: count,
              showTotal: (total, range) =>
                `Showing ${range[1]} of ${total} patients`,
              showQuickJumper: false,
              showLessItems: true,
              onChange: handlePagination,
              itemRender: (page, type, originalElement) => {
                if (type === "prev") {
                  return (
                    <Button
                      type="text"
                      disabled={pagination?.current === 1}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (pagination?.current > 1) {
                          setPagination((prev) => ({
                            ...prev,
                            current: prev.current - 1,
                          }));
                        }
                      }}
                    >
                      Previous
                    </Button>
                  );
                }
                if (type === "next") {
                  const maxPage = Math.ceil(count / pagination?.pageSize);
                  return (
                    <Button
                      type="text"
                      disabled={pagination?.current === maxPage}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (pagination?.current < maxPage) {
                          setPagination((prev) => ({
                            ...prev,
                            current: prev.current + 1,
                          }));
                        }
                      }}
                    >
                      Next
                    </Button>
                  );
                }
                return originalElement;
              },
            }}
            onChange={handleChangeTable}
          />
        </Col>
      </Row>
    </>
  );
};

export default PatientsPage;
